import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listTemplates, listFragments } from '../services/templateService';
import TemplateEditorModal from './TemplateEditorModal';

/**
 * Template Selector — leftpanel section that lets user pick a template.
 * Phase 1: GOYANG only.
 *
 * Props:
 *   - moduleId: string (e.g. 'goyang')
 *   - selectedTemplateId: string | null
 *   - onSelectTemplate: (template | null) => void   // passes resolved template object
 *
 * Preset fragments di-fetch dari preset_fragments collection (single source of truth).
 */
/* `v39` — dipakai bila komponen ini duduk dalam jalur tetapan komposer
   (tab Generate). Ia bukan sekadar tukar warna:

     · BINGKAI + TAJUK + pil WAJIB dalaman DIBUANG. Jalur di luar sudah
       membawa ketiga-tiganya, jadi mengekalkannya menghasilkan dua
       bingkai bersarang dan DUA pil WAJIB pada satu tetapan yang sama
       (dilaporkan user 2026-08-12).
     · Warna beralih ke token --s-*; ungu/putih tetap tidak boleh dibaca
       atas permukaan gelap.

   Lalai `false` — panel rupa lama kekal persis seperti sebelum ini. */
export default function TemplateSelector({ moduleId, selectedTemplateId, onSelectTemplate, refreshKey = 0, v39 = false }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null); // null = new
    // Fragment objects {id, name, ...} dari preset_fragments — canonical source.
    const [hookPresets, setHookPresets] = useState([]);
    const [bgPresets, setBgPresets] = useState([]);
    // Map ID → fragment untuk preview display lookup (fresh name walaupun fragment dah rename).
    const hookById = useMemo(() => {
        const m = new Map();
        hookPresets.forEach(f => m.set(f.id, f));
        return m;
    }, [hookPresets]);
    const bgById = useMemo(() => {
        const m = new Map();
        bgPresets.forEach(f => m.set(f.id, f));
        return m;
    }, [bgPresets]);
    const getHookDisplay = (s) => {
        if (s?.hookId && hookById.has(s.hookId)) return hookById.get(s.hookId).name;
        if (s?.hook === 'Custom') return s.hookCustom ? `Custom: ${s.hookCustom}` : 'Custom';
        return 'Auto (AI decide)';
    };
    const getBgDisplay = (s) => {
        if (s?.bgId && bgById.has(s.bgId)) return bgById.get(s.bgId).name;
        if (s?.bg === 'Custom') return s.bgCustom ? `Custom: ${s.bgCustom}` : 'Custom';
        return 'Auto (AI decide)';
    };

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const list = await listTemplates(moduleId);
            setTemplates(list);
            // Resolve selected template
            const found = list.find(t => t.id === selectedTemplateId);
            if (selectedTemplateId && !found) {
                // Selected ID no longer exists (deleted)
                onSelectTemplate?.(null);
            } else if (found) {
                onSelectTemplate?.(found);
            }
        } catch (err) {
            console.error('[TemplateSelector] Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [moduleId, selectedTemplateId, onSelectTemplate]);

    useEffect(() => {
        fetchList();
        // Fetch preset fragments (hooks + backgrounds) — refetch bila refreshKey bump
        // (admin edit template/fragments di module settings → trigger refresh).
        Promise.all([
            listFragments(moduleId, 'hooks'),
            listFragments(moduleId, 'backgrounds')
        ]).then(([hooks, bgs]) => {
            // Pass full fragment objects {id, name, ...} — TemplateEditorModal lookup by id.
            setHookPresets(hooks.filter(f => f.enabled !== false));
            setBgPresets(bgs.filter(f => f.enabled !== false));
        }).catch(err => console.warn('[TemplateSelector] Failed fetch fragments:', err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleId, refreshKey]);

    const handleSelect = (e) => {
        const id = e.target.value;
        if (!id) {
            onSelectTemplate?.(null);
            return;
        }
        const t = templates.find(x => x.id === id);
        onSelectTemplate?.(t || null);
    };

    const handleNew = () => {
        setEditingTemplate(null);
        setEditorOpen(true);
    };

    const handleEdit = () => {
        const current = templates.find(t => t.id === selectedTemplateId);
        if (!current) return;
        setEditingTemplate(current);
        setEditorOpen(true);
    };

    const handleSaved = async (newId) => {
        await fetchList();
        if (newId) {
            const list = await listTemplates(moduleId);
            const found = list.find(t => t.id === newId);
            if (found) onSelectTemplate?.(found);
        } else {
            // deleted
            onSelectTemplate?.(null);
        }
    };

    const selected = templates.find(t => t.id === selectedTemplateId);
    const editLabel = selected?.isSystem ? 'Clone' : 'Edit';
    // P4: GOYANG WAJIB pilih template. Visual cue (red border + WAJIB pill + helper text)
    // bila user belum pilih supaya jelas ni mandatory, bukan optional.
    const needsSelection = !selectedTemplateId;

    return (
        <div style={{ marginBottom: v39 ? 0 : 14 }}>
            <div style={v39 ? undefined : {
                background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                border: needsSelection ? '1px solid #fca5a5' : '1px solid #ddd6fe',
                borderRadius: 12,
                padding: 12
            }}>
                {!v39 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#8b5cf6' }}>palette</span>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#6d28d9', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Template (GOYANG)</div>
                    {needsSelection && (
                        <span style={{
                            marginLeft: 'auto',
                            fontSize: 9, fontWeight: 800,
                            background: '#fee2e2', color: '#dc2626',
                            padding: '2px 6px', borderRadius: 4,
                            letterSpacing: '0.04em'
                        }}>WAJIB</span>
                    )}
                </div>
                )}

                <select
                    value={selectedTemplateId || ''}
                    onChange={handleSelect}
                    disabled={loading}
                    style={{
                        width: '100%', padding: '8px 10px', fontSize: v39 ? 12.5 : 12, fontWeight: v39 ? 500 : 600,
                        background: v39 ? 'var(--s-card)' : '#ffffff',
                        border: v39
                            ? '1px solid var(--s-line-strong)'
                            : (needsSelection ? '1px solid #ef4444' : '1px solid #c4b5fd'),
                        borderRadius: 8,
                        color: v39 ? 'var(--s-ink)' : '#1e293b', outline: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                        marginBottom: needsSelection ? 6 : 8,
                        ...(v39 ? { fontFamily: 'inherit', boxSizing: 'border-box' } : null),
                    }}
                >
                    <option value="">— Pilih template —</option>
                    {templates.length > 0 && <optgroup label="System Templates">
                        {templates.filter(t => t.isSystem).map(t => (
                            <option key={t.id} value={t.id}>🔒 {t.name}{t.customBgImageUrl ? ' 🖼' : ''}</option>
                        ))}
                    </optgroup>}
                    {templates.some(t => !t.isSystem) && <optgroup label="My Templates">
                        {templates.filter(t => !t.isSystem).map(t => (
                            <option key={t.id} value={t.id}>{t.name}{t.customBgImageUrl ? ' 🖼' : ''}</option>
                        ))}
                    </optgroup>}
                </select>

                {needsSelection && (
                    <div style={{
                        fontSize: v39 ? 11.5 : 10, fontWeight: v39 ? 500 : 600,
                        color: v39 ? 'var(--s-stop)' : '#dc2626',
                        marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4
                    }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>info</span>
                        Pilih template sebelum boleh Generate.
                    </div>
                )}

                {selected && (
                    <div style={{
                        background: v39 ? 'var(--s-card)' : '#ffffff',
                        border: v39 ? '1px solid var(--s-line)' : '1px dashed #c4b5fd',
                        borderRadius: 8, padding: 8, marginBottom: 8,
                        fontSize: v39 ? 11 : 9, lineHeight: 1.6,
                        color: v39 ? 'var(--s-ink-2)' : '#475569',
                    }}>
                        {selected.customBgImageUrl && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6, marginBottom: 6, borderBottom: '1px solid #f1f5f9' }}>
                                <img src={selected.customBgImageUrl} alt="bg" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4, border: '1px solid #fde68a' }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: '#b45309' }}>🖼 Custom BG attached</div>
                                    <div style={{ fontSize: 8, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {selected.customBgFilename || 'background image'}
                                    </div>
                                </div>
                            </div>
                        )}
                        {(() => {
                            const hg = selected.handGender || 'auto';
                            const label = hg === 'male' ? '👨 Lelaki' : (hg === 'female' ? '👩 Perempuan' : '🤖 Auto (AI decide ikut target market)');
                            const color = hg === 'auto' ? '#94a3b8' : '#b45309';
                            return (
                                <div style={{ fontSize: 9, fontWeight: 700, color, marginBottom: 4 }}>
                                    👋 Hand: {label}
                                </div>
                            );
                        })()}
                        <div><b>Set 1:</b> {getHookDisplay(selected.set1)} · <span style={selected.customBgImageUrl ? { textDecoration: 'line-through', color: '#94a3b8' } : {}}>{getBgDisplay(selected.set1)}</span></div>
                        <div><b>Set 2:</b> {getHookDisplay(selected.set2)} · <span style={selected.customBgImageUrl ? { textDecoration: 'line-through', color: '#94a3b8' } : {}}>{getBgDisplay(selected.set2)}</span></div>
                        <div><b>Set 3:</b> {getHookDisplay(selected.set3)} · <span style={selected.customBgImageUrl ? { textDecoration: 'line-through', color: '#94a3b8' } : {}}>{getBgDisplay(selected.set3)}</span></div>
                    </div>
                )}

                <div style={{ display: 'flex', gap: 6 }}>
                    {/* v3.9: butang tidak lagi diregang (`flex: 1`) — dua butang
                        yang memenuhi lebar jalur menjadikan "+ New" nampak
                        seperti tindakan utama jalur ini, sedangkan yang utama
                        ialah MEMILIH template. Lebar tetap, kiri, senyap. */}
                    <button onClick={handleNew} style={v39 ? {
                        flex: '0 0 auto', padding: '6px 12px', fontSize: 12, fontWeight: 500,
                        background: 'var(--s-card)', color: 'var(--s-ink)',
                        border: '1px solid var(--s-line-strong)', borderRadius: 8,
                        cursor: 'pointer', fontFamily: 'inherit',
                    } : {
                        flex: 1, padding: '6px 8px', fontSize: 10, fontWeight: 700,
                        background: '#8b5cf6', color: '#ffffff', border: 'none', borderRadius: 6,
                        cursor: 'pointer', boxShadow: '0 1px 3px rgba(139,92,246,0.25)'
                    }}>
                        + New
                    </button>
                    <button onClick={handleEdit} disabled={!selected} style={v39 ? {
                        flex: '0 0 auto', padding: '6px 12px', fontSize: 12, fontWeight: 500,
                        background: 'transparent',
                        color: selected ? 'var(--s-ink-2)' : 'var(--s-ink-3)',
                        border: '1px solid var(--s-line-strong)', borderRadius: 8,
                        cursor: selected ? 'pointer' : 'not-allowed',
                        opacity: selected ? 1 : 0.6, fontFamily: 'inherit',
                    } : {
                        flex: 1, padding: '6px 8px', fontSize: 10, fontWeight: 700,
                        background: selected ? '#ffffff' : '#f1f5f9',
                        color: selected ? '#6d28d9' : '#94a3b8',
                        border: `1px solid ${selected ? '#c4b5fd' : '#e2e8f0'}`, borderRadius: 6,
                        cursor: selected ? 'pointer' : 'not-allowed'
                    }}>
                        {v39 ? editLabel : `✏ ${editLabel}`}
                    </button>
                </div>
            </div>

            <TemplateEditorModal
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                onSaved={handleSaved}
                moduleId={moduleId}
                hookPresets={hookPresets}
                bgPresets={bgPresets}
                template={editingTemplate}
            />
        </div>
    );
}
